import { useSpring, useSpringRef } from "@react-spring/web";

interface UseToggleSidebarAnimationProps {
  sidebarWidth?: number;
}

const useToggleSidebarAnimation = (
  props: UseToggleSidebarAnimationProps = {},
) => {
  const { sidebarWidth = 260 } = props;

  const sideBarApi = useSpringRef();
  const toggleButtonApi = useSpringRef();

  const sidebarSprings = useSpring({
    ref: sideBarApi,
    from: { width: sidebarWidth },
    config: { tension: 300, friction: 30 },
  });

  const toggleButtonSprings = useSpring({
    ref: toggleButtonApi,
    from: {
      right: -20,
      rotate3d: [1, 0, 0, 0] as [number, number, number, number],
    },
  });

  const isClosed = sidebarSprings.width.to((w) => w === 80);

  const getIsSidebarOpen = () => {
    return sidebarSprings.width.get() === sidebarWidth;
  };

  const getIsSidebarClosed = () => {
    return sidebarSprings.width.get() === 80;
  };

  const getIsSidebarHidden = () => {
    return sidebarSprings.width.get() === 0;
  };

  const getIsToggleButtonOpen = () => {
    return toggleButtonSprings.right.get() === -20;
  };

  const getIsToggleButtonClosed = () => {
    return toggleButtonSprings.right.get() === -40;
  };

  const handleOpenSidebar = () => {
    void sideBarApi.start({ to: { width: sidebarWidth } });
  };

  const handleCloseSidebar = () => {
    void sideBarApi.start({ to: { width: 80 } });
  };

  const handleHideSidebar = () => {
    void sideBarApi.start({ to: { width: 0 } });
  };

  const handleOpenToggleButton = () => {
    void toggleButtonApi.start({
      to: {
        right: -20,
        rotate3d: [1, 0, 0, 0] as [number, number, number, number],
      },
    });
  };

  const handleCloseToggleButton = () => {
    void toggleButtonApi.start({
      to: {
        right: -40,
        rotate3d: [0, 1, 0, 180] as [number, number, number, number],
      },
    });
  };

  const handleHideToggleButton = () => {
    void toggleButtonApi.start({
      to: {
        right: 0,
        rotate3d: [0, 1, 0, 180] as [number, number, number, number],
      },
    });
  };

  const handleToggleSidebar = () => {
    if (getIsSidebarOpen()) {
      handleCloseSidebar();
      handleCloseToggleButton();
    } else {
      handleOpenSidebar();
      handleOpenToggleButton();
    }
  };

  return {
    handleToggleSidebar,
    sidebarSprings,
    sideBarApi,
    toggleButtonApi,
    toggleButtonSprings,
    handleCloseSidebar,
    handleHideSidebar,
    handleCloseToggleButton,
    handleHideToggleButton,
    handleOpenSidebar,
    handleOpenToggleButton,
    getIsSidebarOpen,
    getIsSidebarClosed,
    getIsSidebarHidden,
    getIsToggleButtonOpen,
    getIsToggleButtonClosed,
    isClosed,
  };
};

export default useToggleSidebarAnimation;
